import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, BarChart3, KeyRound, ShoppingBag, TrendingUp } from "lucide-react";
import { track } from "@/lib/analytics";

const OFFERS = [
  {
    n: "01",
    icon: KeyRound,
    title: "Your brand, built",
    body: "Describe your business in one sentence. The Studio Builder drafts your positioning, site copy, offers and launch plan in about 18 minutes.",
    chip: "Studio Builder · all plans",
  },
  {
    n: "02",
    icon: TrendingUp,
    title: "Your content, written",
    body: "A month of social posts, emails and DM follow-ups drafted in your voice, ready to review and publish.",
    chip: "Growth Engine · all plans",
  },
  {
    n: "03",
    icon: ShoppingBag,
    title: "Your storefront, selling",
    body: "A link-in-bio storefront that sells downloads, courses, memberships and bookings around the clock.",
    chip: "Monetization Hub · add-on",
  },
  {
    n: "04",
    icon: BarChart3,
    title: "Your results, measured",
    body: "Leads, published posts and conversions tracked week over week - honest proof that it is working.",
    chip: "Success Insights · all plans",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6 },
};

export default function OfferSection() {
  return (
    <section
      data-testid="offer-section"
      className="py-20 sm:py-28 px-5 sm:px-6 lg:px-12 bg-[#FAF8F2] border-y border-neutral-200/70"
    >
      <div className="max-w-[1400px] mx-auto">
        <motion.div {...fadeUp} className="max-w-3xl">
          <p className="overline">The offer, in plain terms</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.08] mt-5 text-neutral-900">
            One subscription. Four things
            <br className="hidden sm:block" /> done <span className="text-aurora">for you.</span>
          </h2>
          <p className="text-base sm:text-lg text-neutral-500 leading-relaxed mt-6 max-w-2xl">
            No agencies, no five tools taped together. GoldKey Studio builds your
            brand, writes your marketing, gives you a storefront and shows you the
            numbers - from <span className="font-semibold text-neutral-900">$49/mo</span>.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mt-12 sm:mt-16">
          {OFFERS.map((o, i) => (
            <motion.article
              key={o.n}
              {...fadeUp}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              className="bg-white border border-neutral-200 p-7 sm:p-8 hover:border-gold/50 hover:shadow-[0_20px_50px_rgba(10,10,15,0.07)] hover:-translate-y-1 transition-all duration-300 flex flex-col"
              data-testid={`offer-card-${o.n}`}
            >
              <div className="flex items-center justify-between">
                <o.icon size={20} className="text-gold" strokeWidth={1.6} />
                <span className="font-mono text-[10px] tracking-[0.2em] text-neutral-300">{o.n}</span>
              </div>
              <h3 className="font-display text-lg font-bold mt-6 text-neutral-900">{o.title}</h3>
              <p className="text-sm text-neutral-500 leading-relaxed mt-3 flex-1">{o.body}</p>
              <span className="mt-6 inline-flex self-start items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-gold-deep bg-gold/10 border border-gold/25 px-2.5 py-1">
                {o.chip}
              </span>
            </motion.article>
          ))}
        </div>

        <motion.div
          {...fadeUp}
          className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 border-t border-neutral-200/70 pt-8"
        >
          <p className="text-xs uppercase tracking-[0.2em] text-neutral-400">
            From $49/mo · Full access from day one · Cancel anytime
          </p>
          <Link
            to="/pricing"
            data-testid="offer-pricing-cta"
            onClick={() => track("CTA Clicked", { location: "offer_section", label: "See pricing" })}
            className="btn-aurora !py-3 !px-5"
          >
            See pricing & checkout <ArrowUpRight size={14} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
